import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import {
  emptyWhiteboardSnapshot,
  parseWhiteboardSnapshot,
  type WhiteboardSnapshot,
} from "../features/whiteboard/snapshot.js";
import {
  type BoardRow,
  type BoardSummary,
  createBoard,
  deleteBoard,
  getBoard,
  listBoards,
  summarize as summarizeBoard,
  updateBoard,
} from "./boards.js";

/**
 * Whiteboards live in the same `board` table as tactics boards, distinguished
 * by `kind='whiteboard'`. This module is a thin wrapper around the generic
 * board repo that pins the `kind` and parses the whiteboard-shaped snapshot.
 */

export interface WhiteboardSummary
  extends Omit<BoardSummary, "fieldKind" | "orientation" | "frameCount"> {
  bg: WhiteboardSnapshot["bg"];
}

export function summarizeWhiteboard(row: BoardRow): WhiteboardSummary {
  const snap = parseWhiteboardSnapshot(row.snapshot_json);
  const base = summarizeBoard(row);
  return {
    id: base.id,
    name: base.name,
    updatedAt: base.updatedAt,
    cover: snap.cover ?? null,
    bg: snap.bg,
  };
}

export function listWhiteboards(
  db: LocalDb,
  orgId: string,
  ownerId: string,
  ownerKind: OwnerKind = "coach",
): Promise<BoardRow[]> {
  return listBoards(db, orgId, ownerId, ownerKind, "whiteboard");
}

export interface CreateWhiteboardInput {
  orgId: string;
  ownerId: string;
  ownerKind?: OwnerKind;
  name: string;
  snapshot?: WhiteboardSnapshot;
}

export function createWhiteboard(db: LocalDb, input: CreateWhiteboardInput): Promise<BoardRow> {
  return createBoard(db, {
    orgId: input.orgId,
    ownerId: input.ownerId,
    ownerKind: input.ownerKind,
    kind: "whiteboard",
    name: input.name,
    snapshot: input.snapshot ?? emptyWhiteboardSnapshot(),
  });
}

export interface UpdateWhiteboardInput {
  id: string;
  name?: string;
  snapshot?: WhiteboardSnapshot;
}

export function updateWhiteboard(db: LocalDb, input: UpdateWhiteboardInput): Promise<void> {
  return updateBoard(db, {
    id: input.id,
    name: input.name,
    snapshot: input.snapshot,
  });
}

export function deleteWhiteboard(db: LocalDb, args: { id: string; orgId: string }): Promise<void> {
  return deleteBoard(db, args);
}

export function getWhiteboard(db: LocalDb, id: string): Promise<BoardRow | null> {
  return getBoard(db, id);
}
